import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { TAX_CATEGORIES, type TaxCategory } from '@his/shared';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Field, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { TAX_CATEGORY_LABEL } from '@/components/TaxCategorySelect';

export const CITIZEN_RELIEF_REASON = 'sa_citizen_healthcare';

export function LineTaxBadge({ taxCategory, taxReason }: { taxCategory?: string | null; taxReason?: string | null }) {
  if (taxReason === CITIZEN_RELIEF_REASON) {
    return (
      <Badge variant="secondary" className="mt-1">
        {tr('billing', 'vat_borne_by_the_state_saudi_citizen')} <span lang="ar" dir="rtl">ضريبة تتحملها الدولة</span>
      </Badge>
    );
  }
  if (taxReason) {
    return (
      <p className="text-xs text-muted-foreground">
        {tr('billing', 'vat')} {taxCategory ? TAX_CATEGORY_LABEL[taxCategory as TaxCategory] ?? humanize(taxCategory, 'tax_category') : ''}: {taxReason}
      </p>
    );
  }
  return null;
}

export function LineTaxDialog({
  invoiceId,
  line,
  onClose,
  onDone,
}: {
  invoiceId: string;
  line: { id: string; description: string; taxCategory?: string | null };
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [category, setCategory] = useState<TaxCategory>((line.taxCategory as TaxCategory | null) ?? 'standard');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => post(`/invoices/${invoiceId}/lines/${line.id}/tax`, { category, reason: reason.trim() }),
    onSuccess: () => {
      toast.success(tr('billing', 'vat_changed_on_the_line'));
      onDone();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('billing', 'change_the_vat_on_a_line')}
      description={line.description}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('billing', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            loading={save.isPending}
            disabled={reason.trim().length < 5}
          >
            {tr('billing', 'change_vat')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Alert variant="warning">{tr('billing', 'only_on_a_bill_with_no_payments_the_change_and')}</Alert>
        <Field label={tr('billing', 'tax_category')}>
          <ChoiceButtons<TaxCategory>
            layout="chips"
            value={category}
            onChange={setCategory}
            options={TAX_CATEGORIES.map((value) => ({ value, label: TAX_CATEGORY_LABEL[value] }))}
          />
        </Field>
        <Field label={tr('billing', 'reason')} required hint={tr('billing', 'for_example_citizen_id_card_not_shown')}>
          <Textarea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={400} autoFocus />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
